import BaseButton from 'components/atoms/baseButton';
import { useState, type FC } from 'react';
import BaseLoader from 'components/atoms/base-loader';
import BaseRangeDatepicker from 'components/atoms/base-range-datepicker';
import { useGetPatientSnapshot } from 'infrastructure/hooks/patient/use-get-snapshot';
import { useTranslation } from 'react-i18next';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { DateFormatter } from 'infrastructure/functions';
import dayjs from 'dayjs';
import i18n from 'infrastructure/i18n';
import { PresetKeyEnum } from 'infrastructure/enums';

import SummaryModal from './summary-modal';
import ReadingCard from './reading-card';
import s from './styles.module.scss';

import type { IValue } from 'components/atoms/base-range-datepicker';

interface PatientSnapshotProps {
  patientGuid: string;
}

const presets = [
  {
    label: (
      <span data-cy="ranges">
        {i18n.t('dates.lastCountDays', {
          count: 7,
        })}
      </span>
    ),
    value: [dayjs().add(-7, 'd').toDate(), dayjs().endOf('D').toDate()] as [
      Date,
      Date,
    ],
    key: PresetKeyEnum.LastSevenDays,
    index: 7,
  },
  {
    label: (
      <span data-cy="ranges">
        {i18n.t('dates.lastCountDays', {
          count: 30,
        })}
      </span>
    ),
    value: [dayjs().add(-30, 'd').toDate(), dayjs().endOf('D').toDate()] as [
      Date,
      Date,
    ],
    key: PresetKeyEnum.LastThirtyDays,
    index: 30,
  },
  {
    label: (
      <span data-cy="ranges">
        {i18n.t('dates.lastCountMonths', {
          count: 3,
        })}
      </span>
    ),
    value: [dayjs().add(-3, 'M').toDate(), dayjs().endOf('D').toDate()] as [
      Date,
      Date,
    ],
    key: PresetKeyEnum.ThreeMonthsPast,
  },
];

const SUMMARY_BUTTONS = [
  { label: i18n.t('patientSnapshot.previousCall') },
  { label: i18n.t('patientSnapshot.aggregatedData') },
];

const PatientSnapshot: FC<PatientSnapshotProps> = ({ patientGuid }) => {
  const [dates, setDates] = useState<IValue | null>(presets[1].value);
  const [openModalIdx, setOpenModalIdx] = useState<number | null>(null);
  const { patientInfo, loading } = usePatientInfo({ patientGuid });
  const { patientSnapshot, isLoading } = useGetPatientSnapshot({
    patientGuid,
    dates,
  });
  const { t } = useTranslation();
  const twoWaySync = patientInfo?.interactionStatus;

  const loader = (
    <div className={s['loading-box']}>
      <BaseLoader loading />
    </div>
  );

  const content = (
    <>
      <div className={s.header}>
        <div className={s['header-left']}>
          <h3 className={s.title}>{t('patientSnapshot.patientSnapshot')}</h3>
        </div>
        <div className={s['header-right']}>
          <div className={s['patient-info-wrapper']}>
            <p className={s['two-way-wrapper']}>
              {t('labels.2waySync')} -{' '}
              {twoWaySync ? t('controls.yes') : t('controls.no')}
            </p>
            <p className={s['last-contact']}>
              {t('patientSnapshot.lastContact')}:{' '}
              {patientSnapshot?.lastContact ? (
                <>
                  <span>{patientSnapshot?.lastContact.type}</span> at
                  <span>
                    {DateFormatter({ date: patientSnapshot?.lastContact.date })}
                  </span>
                </>
              ) : (
                t('labels.noData')
              )}
            </p>
          </div>
          <div className={s.date}>
            <BaseRangeDatepicker
              withPresets
              value={dates || undefined}
              onChange={(range) => setDates(range ?? null)}
              presets={() => presets}
            />
          </div>
        </div>
      </div>
      <div className={s.body}>
        <div className={s['readings-wrapper']}>
          {patientSnapshot?.data?.map((reading, index) => {
            return (
              <ReadingCard
                key={index}
                type={reading.type}
                values={reading.data}
                statusPercentages={reading.data.statusPercentages}
                trend={reading.data.trend}
              />
            );
          })}
        </div>
        <div className={s.summary}>
          <p>{t('patientSnapshot.summaries')}</p>
          <div>
            {SUMMARY_BUTTONS.map(({ label }, index) => (
              <BaseButton
                type="secondary"
                label={label}
                width={150}
                key={index}
                onClick={() => setOpenModalIdx(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className={s.wrapper}>{loading || isLoading ? loader : content}</div>
      {openModalIdx !== null && (
        <SummaryModal
          open={openModalIdx !== null}
          onCancel={() => setOpenModalIdx(null)}
          subTitle={SUMMARY_BUTTONS[openModalIdx].label}
        />
      )}
    </>
  );
};
export default PatientSnapshot;
